import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import { ROUTES } from 'src/router/const'
import { log } from 'src/services/app/log.service'
import { localStorageService } from 'src/services/app/storage.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { ME_CACHE_TTL_MS, authService } from 'src/services/api/auth.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { ACCOUNT_ERROR_CODE } from 'src/services/consts/account.const'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'

/**
 * Initial account setup mutation for founder onboarding.
 *
 * Persists via `authService.setupAccount` (POST /account/setup), sets the
 * ACCOUNT_DATA query cache, syncs ME_ACCOUNT_CACHE, then broadcasts for
 * cross-tab sync.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function useSetupAccountMutation() {
  const queryClient = useQueryClient()
  const router = useRouter()

  const setupAccountMutation = useMutation({
    mutationKey: [MUTATION_KEYS.SETUP_ACCOUNT],
    mutationFn: ({ accountName, companyName, country }) =>
      authService.setupAccount({ accountName, companyName, country }),

    onSuccess: (accountData) => {
      queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], accountData)
      localStorageService.cacheData(STORAGE_KEYS.ME_ACCOUNT_CACHE, accountData, ME_CACHE_TTL_MS)
      broadcastAccountData(accountData)
      notifyService.success(notifyMsgs.success.setupAccount)
    },

    onError: (error) => {
      log.error('[Account] setupAccount failed', error)

      const code = error?.response?.data?.error?.code

      if (code === ACCOUNT_ERROR_CODE.ACCOUNT_NAME_TAKEN) {
        notifyService.error(notifyMsgs.error.accountNameTaken)
        return
      }

      if (code === ACCOUNT_ERROR_CODE.ACCOUNT_ALREADY_SET_UP) {
        notifyService.info(notifyMsgs.error.accountAlreadySetUp)
        router.push({ name: ROUTES.SETTINGS_ACCOUNT_DETAILS })
        return
      }

      notifyService.error(notifyMsgs.error.setupAccount)
    },
  })

  return {
    setupAccount: wrapMutationAsync(setupAccountMutation.mutateAsync),
    isSettingUpAccount: setupAccountMutation.isPending,
    setupAccountError: setupAccountMutation.error,
  }
}
